import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { _money, _monthLabel } from './utils';
import { VarianceCell } from './VarianceCell';

// iter491 — Grand total strip: per-month + full-year totals for the
// whole report, computed from the three section responses.
export function GrandTotalGrid({ data }) {
  const months = data?.months || [];
  const totals = React.useMemo(() => {
    const acc = {};
    months.forEach(m => { acc[m] = { s1: 0, s2: 0, s3: 0 }; });
    const addFromRows = (rows, key) => (rows || []).forEach(g => (g.sites || []).forEach(s => {
      months.forEach(m => { acc[m][key] += Number(s.months?.[m] || 0); });
    }));
    addFromRows(data?.section1, 's1');
    addFromRows(data?.section2, 's2');
    (data?.section3 || []).forEach(r => months.forEach(m => { acc[m].s3 += Number(r.months?.[m] || 0); }));
    return acc;
  }, [data, months]);

  const yearS1 = data?.totals?.section1_total || 0;
  const yearS2 = data?.totals?.section2_total || 0;
  const yearS3 = data?.totals?.section3_total || 0;
  const yearGrand = data?.totals?.grand_total ?? (yearS1 + yearS2 + yearS3);
  const yearLast = data?.totals?.last_year_total || 0;
  const yearVar = data?.totals?.variance ?? (yearGrand - yearLast);

  const rowDef = [
    { key: 's1', label: 'Section 1 — Defined frequency', year: yearS1, testid: 'grand-row-s1' },
    { key: 's2', label: 'Section 2 — Adhoc / undefined',  year: yearS2, testid: 'grand-row-s2' },
    { key: 's3', label: 'Section 3 — New business',       year: yearS3, testid: 'grand-row-s3' },
  ];

  return (
    <Card data-testid="revenue-budget-grand-total">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Grand Total — All Sections</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <table className="w-full text-xs border-collapse min-w-[900px]">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left px-3 py-2 border-b sticky left-0 bg-slate-100 z-10 min-w-[240px]">Section</th>
              {months.map(m => (
                <th key={m} className="text-right px-2 py-2 border-b whitespace-nowrap">{_monthLabel(m)}</th>
              ))}
              <th className="text-right px-2 py-2 border-b border-l bg-slate-50 whitespace-nowrap">Full year</th>
            </tr>
          </thead>
          <tbody>
            {rowDef.map(r => (
              <tr key={r.key} data-testid={r.testid}>
                <td className="px-3 py-2 border-b sticky left-0 bg-white z-10">{r.label}</td>
                {months.map(m => (
                  <td key={m} className={`px-2 py-2 text-right border-b whitespace-nowrap ${totals[m][r.key] ? '' : 'text-slate-300'}`}
                    data-testid={`${r.testid}-${m}`}>
                    {totals[m][r.key] ? _money(totals[m][r.key]) : '—'}
                  </td>
                ))}
                <td className="px-2 py-2 text-right border-l border-b bg-slate-50/40 font-medium whitespace-nowrap"
                  data-testid={`${r.testid}-year`}>{_money(r.year)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-900 text-white font-bold">
            <tr data-testid="grand-total-row">
              <td className="px-3 py-2 sticky left-0 bg-slate-900 z-10 uppercase text-[11px] tracking-wide">Grand total</td>
              {months.map(m => {
                const v = totals[m].s1 + totals[m].s2 + totals[m].s3;
                return (
                  <td key={m} className={`px-2 py-2 text-right whitespace-nowrap ${v ? '' : 'text-slate-500'}`}
                    data-testid={`grand-total-${m}`}>
                    {v ? _money(v) : '—'}
                  </td>
                );
              })}
              <td className="px-2 py-2 text-right border-l bg-slate-800 whitespace-nowrap"
                data-testid="grand-total-year">{_money(yearGrand)}</td>
            </tr>
          </tfoot>
        </table>
        {yearLast > 0 && (
          <div className="px-4 py-2 flex items-center justify-end gap-4 text-[11px] border-t bg-slate-50">
            <span className="text-slate-500">Last year:</span>
            <span className="font-semibold">{_money(yearLast)}</span>
            <span className="text-slate-500">Δ vs LY:</span>
            <span data-testid="grand-total-variance"><VarianceCell variance={yearVar} /></span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
